import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@repo/ui/components/form';
import { Input } from '@repo/ui/components/input';
import { Label } from '@repo/ui/components/label';
import { Switch } from '@repo/ui/components/switch';
import { useFormContext } from 'react-hook-form';

import { fieldLabel, fieldSurface } from '../auth/AuthFrame';
import type { TaxValues } from './tax';

/**
 * The GST pair on a price, for the new-item and reprice forms: a switch, and
 * the HSN code and rate under it. The rule lives in `tax.ts`; this is only how
 * it is set.
 *
 * Read from the surrounding registry `Form` rather than handed a `control`,
 * because the two forms' value types differ everywhere but here and a `Control`
 * of one is not a `Control` of the other.
 */
export function TaxFields() {
  const { control, watch } = useFormContext<TaxValues>();
  const chargesGst = watch('chargesGst');

  return (
    <>
      {/* `text-foreground` on `bg-muted`, never the muted pairing, which misses
          AA — the same inset as Counts stock. */}
      <FormField
        control={control}
        name="chargesGst"
        render={({ field }) => (
          <div className="flex items-start gap-3 rounded-2xl bg-muted px-4 py-3">
            <Switch
              id="charges-gst"
              aria-labelledby="charges-gst-label"
              className="mt-0.5"
              checked={field.value}
              onCheckedChange={(checked) => {
                field.onChange(checked);
              }}
            />
            <div className="space-y-1">
              <Label
                id="charges-gst-label"
                htmlFor="charges-gst"
                className="cursor-pointer text-sm text-foreground"
              >
                Charges GST
              </Label>
              <p className="text-sm text-foreground/70">
                On for an item a tax invoice lists, with its HSN code and rate. Off and it is not
                taxed at all — which is not the same as a 0% rate. Only goods sold at a shop counter
                print a tax invoice; tickets do not yet.
              </p>
            </div>
          </div>
        )}
      />

      {chargesGst && (
        <>
          <FormField
            control={control}
            name="hsnCode"
            render={({ field }) => (
              <FormItem>
                <FormLabel required className={fieldLabel}>
                  HSN or SAC code
                </FormLabel>
                <FormControl
                  render={
                    <Input
                      className={`${fieldSurface} h-11 font-mono tracking-wider numeric`}
                      required
                      inputMode="numeric"
                      placeholder="9985"
                      autoComplete="off"
                      {...field}
                    />
                  }
                />
                <FormDescription>
                  Printed against this item on every tax invoice. 4, 6 or 8 digits.
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={control}
            name="taxRate"
            render={({ field }) => (
              <FormItem>
                <FormLabel required className={fieldLabel}>
                  GST rate
                </FormLabel>
                <div className="relative">
                  <FormControl
                    render={
                      <Input
                        className={`${fieldSurface} h-11 pr-9 font-mono numeric`}
                        required
                        inputMode="decimal"
                        placeholder="18"
                        autoComplete="off"
                        {...field}
                      />
                    }
                  />
                  <span
                    // Decorative: the label says it is a rate.
                    aria-hidden
                    className="pointer-events-none absolute top-1/2 right-3.5 -translate-y-1/2 text-muted-foreground"
                  >
                    %
                  </span>
                </div>
                <FormDescription>
                  The selling price already includes this GST. Tax is worked out downward from the
                  price, never added on top.
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
        </>
      )}
    </>
  );
}
